/**
 * Configuration object defining permitted roles and entity ID requirements for various management actions.
 *
 * Each property represents an action and contains:
 * - roles: An array of role IDs allowed to perform the action.
 * - entityID: A boolean indicating if access to a specific entity (e.g., org, site, team) must also be validated.
 *
 * Role hierarchy:
 * - 201 (Developer)        → Bypassed entirely in the hook (always returns true). Listed here for documentation clarity.
 * - 101 (Global Admin)     → Can perform all platform actions except system-level destructive ops (deleteOrg, deleteDevice, editJsonSetting).
 * - 111 (Installer)        → Partner installer — org-wide near-admin. Everything 101 can do EXCEPT billing surfaces,
 *                            developer-only ops (deleteOrg, demoReporting, editJsonSetting, editCredentialSetting)
 *                            and bookings management (day-1 carve-out per installer-role-plan §3.2).
 * - 103 (Site Admin)       → Full control within their assigned site.
 * - 104 (Floor Admin)      → Manage people and devices within their floor.
 * - 105 (Team Admin)       → Manage team membership and team-level permissions.
 * - 107 (Assets Admin)     → Manage devices, categories, and groups.
 * - 108 (Global Read Only) → View only. Not present in any CUD action → hook returns false automatically.
 * - 109 (Site Read Only)   → View only. Not present in any CUD action → hook returns false automatically.
 * - 202 (Asset Manager)    → Read-only on assets. Not present in any CUD action → hook returns false automatically.
 * - 203 (Inventory Manager)→ Read-only on inventory. Not present in any CUD action → hook returns false automatically.
 * - 1   (User)             → Can only update their own profile (personInformationCUD, updatePerson handled separately via usePermittedUpdatePerson).
 *
 * @typedef {Object} ManagePermittedRolesConfig
 * @property {Object.<string, {roles: number[], entityID: boolean}>} actions
 *
 * @type {ManagePermittedRolesConfig}
 */

export const managePermittedRolesConfig = {
	// ─── Organisation ────────────────────────────────────────────────────────
	updateOrg: { roles: [101, 111], entityID: true },
	deleteOrg: { roles: [201], entityID: false },
	orgInformationCUD: { roles: [101, 111], entityID: true },

	// ─── Sites ───────────────────────────────────────────────────────────────
	manageSiteAssignment: { roles: [101, 111, 103], entityID: false },
	addSite: { roles: [101, 111], entityID: false },
	updateSite: { roles: [101, 111, 103], entityID: true },
	deleteSite: { roles: [101, 111], entityID: true },
	editSite: { roles: [101, 111], entityID: false },
	removeSite: { roles: [101, 111], entityID: false },
	editSiteMap: { roles: [101, 111], entityID: false },
	siteInformationCUD: { roles: [101, 111, 103], entityID: true },

	// ─── Floors / Spaces ─────────────────────────────────────────────────────
	addFloor: { roles: [101, 111, 103], entityID: false },
	updateFloor: { roles: [101, 111, 103, 104], entityID: true },
	deleteFloor: { roles: [101, 111, 103], entityID: true },
	floorInformationCUD: { roles: [101, 111, 103, 104], entityID: true },
	editFloorPlan: { roles: [101, 111, 103, 104], entityID: true },
	// Spaces (new terminology — same entity as Floors)
	addSpace: { roles: [101, 111, 103], entityID: false },
	editSpace: { roles: [101, 111, 103], entityID: false },
	removeSpace: { roles: [101, 111, 103], entityID: false },
	editSpaceMap: { roles: [101, 111, 103], entityID: false },

	// ─── People ──────────────────────────────────────────────────────────────
	addPerson: { roles: [101, 111, 103, 104, 105], entityID: false },
	updatePerson: { roles: [101, 111, 103, 104, 105, 1], entityID: false },
	deletePerson: { roles: [101, 111, 103, 201], entityID: false },
	enablePerson: { roles: [101, 111, 103, 104, 105], entityID: false },
	disablePerson: { roles: [101, 111, 103, 104, 105], entityID: false },
	resetPasswordPerson: { roles: [101, 111, 103], entityID: false },
	personInformationCUD: { roles: [101, 111, 103, 104, 1], entityID: false },
	manageAssociatedPeople: { roles: [101, 111, 103], entityID: false },

	// ─── Teams ───────────────────────────────────────────────────────────────
	addTeam: { roles: [101, 111, 103, 104, 105], entityID: false },
	updateTeam: { roles: [101, 111, 103, 105], entityID: false },
	deleteTeam: { roles: [101, 111, 103, 105], entityID: false },
	manageMembers: { roles: [101, 111, 103, 105], entityID: false },
	teamInformationCUD: { roles: [101, 111, 103, 105], entityID: false },

	// ─── Devices ─────────────────────────────────────────────────────────────
	addDevice: { roles: [101, 111, 103, 104, 107], entityID: false },
	updateDevice: { roles: [101, 111, 103, 104, 107], entityID: false },
	manageDeviceSites: { roles: [101, 111, 103], entityID: false },
	/*
	 * Install photos on a device (the media broker's `device` entity).
	 *
	 * 🛑 NOT `updateDevice`, and the difference is not cosmetic. The server gates device media on
	 * `hasWriteCapability` — developer, global admin, installer, or a caller with SITE scope — and
	 * site scope is populated only from Site-entity roles. `updateDevice` additionally lists 104
	 * (Floor Admin) and 107 (Assets Admin), neither of which earns site scope, so reusing it showed
	 * those two an enabled "Add photos" button, a dashed add tile, and a 403 the moment they picked a
	 * file. This list mirrors what the broker will actually accept.
	 */
	manageDeviceMedia: { roles: [101, 111, 103], entityID: false },
	// Site photos. Not `editSiteMap` — that gates the plan and stops at 101/111.
	manageSiteMedia: { roles: [101, 111, 103], entityID: false },
	deleteDevice: { roles: [101, 111, 201], entityID: false },
	revokeDeviceAccess: { roles: [101, 111, 103, 104], entityID: false },
	disableDevice: { roles: [101, 111, 103, 104, 107], entityID: false },
	logMaintenance: { roles: [101, 111, 103, 104, 107], entityID: false },
	// Live "Ping bound locks" — wakes BLE via the gateway, so gate to managers/admins
	// (excludes read-only roles); also rate-limited per gateway in the UI.
	pingGatewayLocks: { roles: [101, 111, 103, 104, 107], entityID: false },
	// Gateway rename + firmware-check (Phase D) — manager/admin (low risk).
	manageGateway: { roles: [101, 111, 103, 107], entityID: false },
	// Gateway network (IP) config edit (Phase D) — elevated admin ONLY; a bad value can
	// disconnect the gateway.
	editGatewayNetwork: { roles: [101, 111], entityID: false },

	// ─── Categories ──────────────────────────────────────────────────────────
	addCategory: { roles: [101, 111, 103, 104, 107], entityID: false },
	updateCategory: { roles: [101, 111, 103, 104, 107], entityID: false },
	deleteCategory: { roles: [101, 111, 103, 107], entityID: false },

	// ─── Credentials ─────────────────────────────────────────────────────────
	addCredential: { roles: [101, 111, 103, 104, 107], entityID: false },
	updateCredential: { roles: [101, 111, 103, 104], entityID: false },
	deleteCredential: { roles: [101, 111, 201], entityID: false },

	// ─── Device Groups ───────────────────────────────────────────────────────
	createGroup: { roles: [101, 111, 103, 104, 107], entityID: false },
	updateGroup: { roles: [101, 111, 103, 104, 107], entityID: false },
	deleteGroup: { roles: [101, 111, 103, 104, 107], entityID: false },
	revokeGroupAccess: { roles: [101, 111, 103, 104], entityID: false },
	disableGroup: { roles: [101, 111, 103, 104, 107], entityID: false },

	// ─── Bookings ────────────────────────────────────────────────────────────
	addBooking: { roles: [101, 103, 104], entityID: false },
	updateBooking: { roles: [101, 103, 104], entityID: false },
	deleteBooking: { roles: [101, 103], entityID: false },

	// ─── Access Control ──────────────────────────────────────────────────────
	aCRemoteUnlock: { roles: [101, 111, 103], entityID: false },
	aCPermissionManagement: { roles: [101, 111, 103, 104], entityID: false },
	// NOTE: installer device grant authority is NOT a managePermittedRoles action — a flat role list
	// can't express its load-bearing precedence (111 is a hard deny checked BEFORE the 103 site path,
	// so 111+103 is denied). It lives in library/helpers/installerGrantAuthority.js, the exact mirror
	// of backend helpers/authorizeInstallerGrant.js. Do not re-add a `manageInstallerGrants` entry here.

	// ─── System / Developer Only ─────────────────────────────────────────────
	demoReporting: { roles: [201], entityID: false },
	editJsonSetting: { roles: [201], entityID: false },
	editCredentialSetting: { roles: [201], entityID: false },
	editSettings: { roles: [101, 111, 103], entityID: false },
	// Must match the audit-log route's `access` (Navigation/routes.jsx).
	viewAuditLog: { roles: [101, 201], entityID: false },

	// ─── Admin Role Checks ───────────────────────────────────────────────────
	isOrgAdmin: { roles: [101, 111], entityID: false },
	isSiteOrOrgAdmin: { roles: [101, 111, 103], entityID: false },

	// ─── Dashboard ───────────────────────────────────────────────────────────
	// Only roles that should see the dashboard at all — excludes basic USER (role 1).
	viewDashboard: { roles: [101, 111, 103, 104, 107, 108, 109], entityID: false },
	viewRoles: { roles: [101, 111, 103, 104, 105], entityID: false },
	assignRole: { roles: [101, 111, 103, 104, 105], entityID: false },
	revokeRole: { roles: [101, 111], entityID: false }
};
